"use client";
/**
 * Minimal shared UI primitives.
 * No external component library — plain Tailwind classes.
 */

import React from "react";
import { NustiiLogo } from "@/components/auth/NustiiLogo";

// ── Button ────────────────────────────────────────────────────────────────────

type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";

const VARIANT_CLS: Record<ButtonVariant, string> = {
  primary:
    "bg-[#FF7100] hover:bg-[#E06300] text-white disabled:bg-[#FFB87A]",
  secondary:
    "bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 disabled:opacity-50",
  danger:
    "bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300",
  ghost:
    "text-[#FF7100] hover:underline disabled:opacity-50 px-0 py-0",
};

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  loading?: boolean;
}

export function Button({
  variant = "primary",
  loading = false,
  children,
  className = "",
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF7100] focus:ring-offset-2 ${VARIANT_CLS[variant]} ${className}`}
      {...rest}
    >
      {loading && (
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      )}
      {children}
    </button>
  );
}

// ── Input ─────────────────────────────────────────────────────────────────────

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
}

export function Input({ label, error, id, className = "", ...rest }: InputProps) {
  const inputId = id ?? label.toLowerCase().replace(/\s+/g, "-");
  return (
    <div className="space-y-1">
      <label htmlFor={inputId} className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      <input
        id={inputId}
        className={`block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#FF7100] ${
          error ? "border-red-400" : "border-gray-300"
        } ${className}`}
        {...rest}
      />
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

// ── Select ────────────────────────────────────────────────────────────────────

export interface SelectProps
  extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  error?: string;
  options: { value: string; label: string }[];
}

export function Select({
  label,
  error,
  options,
  id,
  className = "",
  ...rest
}: SelectProps) {
  const inputId = id ?? label.toLowerCase().replace(/\s+/g, "-");
  return (
    <div className="space-y-1">
      <label htmlFor={inputId} className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      <select
        id={inputId}
        className={`block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#FF7100] ${
          error ? "border-red-400" : "border-gray-300"
        } ${className}`}
        {...rest}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

// ── Alert ─────────────────────────────────────────────────────────────────────

export function ErrorAlert({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      {message}
    </div>
  );
}

export function SuccessAlert({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
      {message}
    </div>
  );
}

// ── Card ──────────────────────────────────────────────────────────────────────

export function Card({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`rounded-lg border border-gray-200 bg-white p-4 shadow-sm ${className}`}>
      {children}
    </div>
  );
}

// ── Spinner ───────────────────────────────────────────────────────────────────

export function Spinner({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-gray-500">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#FF7100] border-t-transparent" />
      {label}
    </div>
  );
}

// ── PageHeader ────────────────────────────────────────────────────────────────
//
// Mobile-responsive: on very narrow viewports (< ~400 px) the title block and
// action buttons wrap onto separate rows so nothing overflows the viewport.
// Desktop layout (title left, actions right on the same row) is preserved at
// sm: and wider.
//
// Key classes:
//   flex-wrap        — allow wrapping when combined width exceeds container
//   min-w-0          — title div can shrink below its natural content width
//   overflow-hidden  — prevents a long title from pushing actions out
//   truncate         — ellipsis on the title itself if still too long
//   shrink-0         — action group never collapses; it wraps below instead

export function PageHeader({
  title,
  subtitle,
  action,
  showLogo = false,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  showLogo?: boolean;
}) {
  return (
    <header className="space-y-1">
      {showLogo && (
        <div className="mb-1">
          <NustiiLogo size="sm" variant="light" />
        </div>
      )}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold tracking-tight text-[#0D1117] truncate">{title}</h1>
          {subtitle && (
            <p className="mt-0.5 text-sm text-gray-500 truncate">{subtitle}</p>
          )}
        </div>
        {action && (
          <div className="shrink-0 flex flex-wrap gap-2">{action}</div>
        )}
      </div>
    </header>
  );
}
